import { DS_SHAPE_BORDER_RADIUS } from '@calm/evexia-design-tokens';
import { InfoOutlined } from '@mui/icons-material';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Divider,
    IconButton,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { LinearGraphAxis, LinearGraphProps } from './types';

const styleContainer = { width: '100%' };
const styleGraphContainer = { width: '100%', px: 2 };
const styleLabelTrack = {
    width: '100%',
    position: 'relative',
    height: '64px',
};
const styleScale = {
    width: '100%',
    height: '32px',
    borderRadius: `${DS_SHAPE_BORDER_RADIUS.MEDIUM}px`,
    background:
        'linear-gradient(270deg, #F0758A 0%, #F0A875 24.48%, #F0D975 51.04%, #75F094 75.52%, #75B2F0 100%)',
};
const styleAxisTrack = { width: '100%', position: 'relative', height: '32px' };
const styleAxisLabel = { position: 'absolute', width: '16px' };
const styleAxisLabelLine = { height: '8px', mx: '7px' };
const styleAxisLabelText = {
    textAlign: 'center',
    width: '16px',
    display: 'block',
};
const styleValueLabel = {
    py: 1,
    px: 4,
    position: 'absolute',
    textAlign: 'center',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'info.light' : 'info.dark',
    '::after': {
        position: 'absolute',
        borderTopColor: (theme: Theme) =>
            `${
                theme.palette.mode === 'light'
                    ? theme.palette.info.light
                    : theme.palette.info.dark
            } !important`,
        content: '""',
        width: 0,
        height: 0,
        borderTop: '16px solid',
    },
};
const styleValueLabelLeft = {
    ...styleValueLabel,
    borderRadius: `${DS_SHAPE_BORDER_RADIUS.SMALL}px ${DS_SHAPE_BORDER_RADIUS.SMALL}px ${DS_SHAPE_BORDER_RADIUS.SMALL}px 0`,
    '::after': {
        ...styleValueLabel['::after'],
        left: 0,
        borderRight: '16px solid transparent',
    },
};
const styleValueLabelRight = {
    ...styleValueLabel,
    borderRadius: `${DS_SHAPE_BORDER_RADIUS.SMALL}px ${DS_SHAPE_BORDER_RADIUS.SMALL}px 0 ${DS_SHAPE_BORDER_RADIUS.SMALL}px`,
    transform: 'translateX(-100%)',
    '::after': {
        ...styleValueLabel['::after'],
        right: 0,
        borderLeft: '16px solid transparent',
    },
};
const styleLabelText = {
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'info.dark' : 'info.light',
};

const styleHeader = {
    mb: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
};

const styleInfoButton = {
    p: 1,
};

const styleSubtitleWrapper = {
    mb: 2,
};

export function LinearGraph({
    title,
    subtitle,
    trackInfoButton = 'NotMapped',
    onInfoClick,
    min,
    max,
    value,
    valueLabel,
    axis: _axis,
}: LinearGraphProps) {
    const { formatMessage } = useIntl();
    const axis = useMemo<LinearGraphAxis | undefined>(
        () => _axis?.map((y) => [y, y.toString()]),
        [_axis]
    );
    const calculatedAxis: LinearGraphAxis = useMemo(
        () =>
            axis ?? [
                [min, min.toString()],
                [max, max.toString()],
            ],
        [axis, max, min]
    );
    const styleLabelByPosition = useMemo(
        () => (value / max > 0.5 ? styleValueLabelRight : styleValueLabelLeft),
        [max, value]
    );

    return (
        <Box sx={styleContainer}>
            {(title || onInfoClick) && (
                <>
                    <Box sx={styleHeader}>
                        {title && (
                            <Typography
                                component="h2"
                                variant="body1"
                                color="text.body"
                                fontWeight={subtitle ? 'bold' : 'normal'}
                            >
                                {title}
                            </Typography>
                        )}
                        {onInfoClick && (
                            <IconButton
                                trackButton={trackInfoButton}
                                size="small"
                                aria-label={formatMessage(
                                    commonMessages['common.graph_info'],
                                    {
                                        title,
                                    }
                                )}
                                onClick={onInfoClick}
                                sx={styleInfoButton}
                            >
                                <InfoOutlined />
                            </IconButton>
                        )}
                    </Box>
                    {subtitle && (
                        <Box sx={styleSubtitleWrapper}>
                            <Typography variant="body1" color="text.sub">
                                {subtitle}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
            <Box
                sx={styleGraphContainer}
                role="figure"
                aria-label={formatMessage(
                    commonMessages['common.linear_graph'],
                    {
                        valueLabel,
                        value,
                        min,
                        max,
                    }
                )}
            >
                <Box sx={styleLabelTrack}>
                    <Box
                        sx={sxAppend(styleLabelByPosition, {
                            left: `${(value / max) * 100}%`,
                        })}
                        aria-hidden
                    >
                        <Typography
                            component="div"
                            sx={styleLabelText}
                            variant="subtitle2"
                        >
                            {valueLabel}
                        </Typography>
                        <Typography
                            component="div"
                            sx={styleLabelText}
                            variant="subtitle2"
                        >
                            {value}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={styleScale}></Box>
                <Box sx={styleAxisTrack} aria-hidden>
                    {calculatedAxis.map(([position, label], i) => (
                        <Box
                            key={i}
                            sx={sxAppend(styleAxisLabel, {
                                left: `calc(${(position / max) * 100}% - 8px)`,
                            })}
                        >
                            <Divider
                                orientation="vertical"
                                sx={styleAxisLabelLine}
                            />
                            <Typography
                                variant="caption"
                                color="text.sub"
                                sx={styleAxisLabelText}
                            >
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
